import { useContext, useEffect } from 'react';

import { useGetOrganizationClientsGrowthMutation } from '@app/api/Analytics';
import { useGetBranchesForSystemQuery } from '@app/api/Branches';
import { useMemoizeObject, useUserPrint } from '@app/hooks';
import type { UseOrganizationClientsGrowthEffectsProps } from '@app/pages/Analytics/OrganizationClientsGrowth/OrganizationClientsGrowth.types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

export const useOrganizationClientsGrowthEffects = ({
  setSelectedBranches,
  chartDataParams,
}: UseOrganizationClientsGrowthEffectsProps) => {
  const { activeBranchId } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);

  const {
    data: branches,
    isLoading: isBranchesLoading,
    error: branchesError,
  } = useGetBranchesForSystemQuery(
    { ActiveBranchId: activeBranchId },
    { skip: !activeBranchId }
  );
  const [
    getOrganizationClientsGrowth,
    {
      data: organizationClientsGrowth,
      isLoading: organizationClientsGrowthLoading,
    },
  ] = useGetOrganizationClientsGrowthMutation();

  const isLoading = useMemoizeObject(
    organizationClientsGrowthLoading || isBranchesLoading
  );

  useEffect(() => {
    if (branches) {
      const defaultBranch = branches.find(({ Id }) => Id === activeBranchId);
      defaultBranch && setSelectedBranches([{ value: defaultBranch.Id }]);
    }
  }, [activeBranchId, branches, setSelectedBranches]);

  useEffect(() => {
    if (!activeBranchId) return;

    const fetchOrganizationClientsGrowth = async () => {
      try {
        await getOrganizationClientsGrowth({
          ActiveBranchId: activeBranchId,
          data: chartDataParams,
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    };

    fetchOrganizationClientsGrowth();
  }, [
    activeBranchId,
    chartDataParams,
    getOrganizationClientsGrowth,
    showNotification,
  ]);

  useEffect(() => {
    if (branchesError) {
      showNotification(handleCatchError(branchesError));
    }
  });

  return { branches, isLoading, organizationClientsGrowth };
};
